import type { LDFlagSet } from 'launchdarkly-react-client-sdk';

import account from '@/assets/icons/vn-icon-account.svg';
import accountActivity from '@/assets/icons/vn-icon-account-activity.svg';
import adTag from '@/assets/icons/vn-icon-ad-tag-management.svg';
import admin from '@/assets/icons/vn-icon-admin.svg';
import adminLegacy from '@/assets/icons/vn-icon-admin-legacy.svg';
import advancedAdmin from '@/assets/icons/vn-icon-advanced-admin.svg';
import analytics from '@/assets/icons/vn-icon-analytics.svg';
import liveEvents from '@/assets/icons/vn-icon-app-events.svg';
import assetLibrary from '@/assets/icons/vn-icon-asset-library.svg';
import transitionPackages from '@/assets/icons/vn-icon-automated-transitions.svg';
import billing from '@/assets/icons/vn-icon-billing.svg';
import channelManager from '@/assets/icons/vn-icon-channel-manager.svg';
import cmsUser from '@/assets/icons/vn-icon-cms-users.svg';
import content from '@/assets/icons/vn-icon-content.svg';
import dashboards from '@/assets/icons/vn-icon-dashboards.svg';
import destinations from '@/assets/icons/vn-icon-destinations.svg';
import globalInterstitials from '@/assets/icons/vn-icon-global-interstitials.svg';
import health from '@/assets/icons/vn-icon-health.svg';
import inputs from '@/assets/icons/vn-icon-inputs-scheduler.svg';
import live from '@/assets/icons/vn-icon-live.svg';
import liveGuideIcon from '@/assets/icons/vn-icon-live-guide.svg';
import liveGuide from '@/assets/icons/vn-icon-live-guide.svg';
import liveProgram from '@/assets/icons/vn-icon-live-programs.svg';
import marketing from '@/assets/icons/vn-icon-marketing.svg';
import monetization from '@/assets/icons/vn-icon-monetization.svg';
import monitoringDashboard from '@/assets/icons/vn-icon-monitoring.svg';
import movies from '@/assets/icons/vn-icon-movies.svg';
import mrssFeed from '@/assets/icons/vn-icon-mrss-feed-config.svg';
import mrssMapper from '@/assets/icons/vn-icon-mrss-mapper.svg';
import navigation from '@/assets/icons/vn-icon-ou-navigation.svg';
import products from '@/assets/icons/vn-icon-products.svg';
import pushNotifications from '@/assets/icons/vn-icon-push-notifications.svg';
import series from '@/assets/icons/vn-icon-series.svg';
import settings from '@/assets/icons/vn-icon-settings.svg';
import siteSettings from '@/assets/icons/vn-icon-site-settings.svg';
import subscribers from '@/assets/icons/vn-icon-subscribers.svg';
import uploadAsset from '@/assets/icons/vn-icon-upload-assets.svg';
import usage from '@/assets/icons/vn-icon-usage.svg';
import workflowConfig from '@/assets/icons/vn-icon-workflow-config.svg';
import type { OU } from '@/models/Ou';
import type { Permissions } from '@/models/Permissions';

import { Permission } from '../../../enums';

type SlidebarItem = {
  id: string;
  text: string;
  path: string;
  permission: Permission;
  icon: JSX.Element;
  children: SlidebarItem[];
  featureFlagEnable: boolean;
  onClick?: (e: MouseEvent) => void;
};

const getSideBarList = (ouData: OU, permissions: Permissions, flags: LDFlagSet) => {
  return [
    {
      id: 'dashboards',
      text: 'Dashboards',
      path: '',
      icon: <img src={dashboards} alt="Dashboards" />,
      children: [
        {
          id: 'analytics',
          text: 'Analytics',
          path: '/dashboards/analytics',
          permission: Permission.AnalyticsDashboard,
          icon: <img src={analytics} alt="Analytics" />,
        },
        {
          id: 'monitoringDashboard',
          text: 'Monitoring',
          path: '/dashboards/stream-monitoring',
          permission: Permission.Dashboard,
          icon: <img src={monitoringDashboard} alt="Monitoring" />,
        },
        {
          id: 'health',
          text: 'Health',
          path: '/dashboards/health',
          permission: Permission.OUSuperAdmin,
          icon: <img src={health} alt="Health" />,
        },
        {
          id: 'usage',
          text: 'Usage',
          path: '/dashboards/usage',
          permission: Permission.OUSuperAdmin,
          icon: <img src={usage} alt="Usage" />,
        },
        {
          id: 'destinations',
          text: 'Destinations',
          path: '/dashboards/destinations',
          permission: Permission.OUSuperAdmin,
          icon: <img src={destinations} alt="Destinations" />,
        },
        {
          id: 'app-analytics',
          text: 'App Analytics',
          path: '/dashboards/app-analytics',
          permission: Permission.AnalyticsDashboard,
          icon: <img src={analytics} alt="Analytics" />,
        },
      ],
    },
    {
      id: 'uploadAssets',
      text: 'Upload Assets',
      path: '/assets/new',
      permission: Permission.Ingest,
      icon: <img src={uploadAsset} alt="Upload Assets" />,
    },
    {
      id: 'assetLibrary',
      text: 'Asset Library',
      path: '/assets/list',
      permission: Permission.ListSearchAssets,
      icon: <img src={assetLibrary} alt="Asset Library" />,
    },
    {
      id: 'channelManager',
      text: 'Channel Manager',
      path: '/channelmanager/list',
      permission: Permission.ChannelManager,
      icon: <img src={channelManager} alt="Channel Manager" />,
      children: [],
    },
    {
      id: 'live',
      text: 'Live',
      icon: <img src={live} alt="Live" />,
      children: [
        {
          id: 'livePrograms',
          text: 'Stream Key Setup',
          path: '/live/programs/list',
          icon: <img src={liveProgram} alt="Stream Key Setup" />,
        },
        {
          id: 'inputs',
          text: 'Inputs Scheduler',
          path: '/live/inputs/list',
          permission: Permission.OUSuperAdmin,
          icon: <img src={inputs} alt="Inputs Scheduler" />,
        },
        {
          id: 'appEvents',
          text: 'App Events',
          path: '/live/events/list',
          permission: Permission.ListSearchAppEvents,
          icon: <img src={liveEvents} alt="App Events" />,
        },
        {
          id: 'liveGuide',
          text: 'Live Guide',
          path: '/live/guide/list',
          permission: Permission.ListSearchAppEvents,
          icon: <img src={liveGuideIcon} alt="Live Guide" />,
        },
      ],
    },
    {
      id: 'content',
      text: 'Content',
      icon: <img src={content} alt="Content" />,
      permission: Permission.Ingest,
      children: [
        {
          id: 'movies',
          text: 'Movies',
          path: '/movies/list',
          icon: <img src={movies} alt="Movies" />,
          children: [],
        },
        {
          id: 'series',
          text: 'Series',
          path: '/series/list',
          icon: <img src={series} alt="Series" />,
        },
      ],
    },
    {
      id: 'epg',
      text: 'EPG',
      path: '',
      icon: <img src={liveGuide} alt="EPG" />,
    },
    {
      id: 'monetization-dropdown',
      text: 'Monetization',
      icon: <img src={monetization} alt="Monetization" />,
      children: [
        {
          id: 'monetization',
          text: 'Ad Tags Management',
          path: '/ads',
          icon: <img src={adTag} alt="Ad Tags Management" />,
          permission: Permission.AdManagement,
          featureFlagEnable: flags['fuel-ui-show-self-service-ads'],
        },
        {
          id: 'products',
          text: 'Products',
          path: '/monetization/products/list',
          permission: Permission.ListSearchProducts,
          icon: <img src={products} alt="Products" />,
        },
        {
          id: 'productCoupons',
          text: 'Coupons',
          path: '/monetization/coupons/list',
          permission: Permission.ListSearchProducts,
          icon: <img src={products} alt="Coupons" />,
        },
      ],
    },
    {
      id: 'marketing',
      text: 'Marketing',
      icon: <img src={marketing} alt="Marketing" />,
      children: [
        {
          id: 'pushNotifications',
          text: 'Push Notifications',
          path: '/marketing/push-notifcations',
          permission: Permission.ListSearchPushNotifications,
          icon: <img src={pushNotifications} alt="Push Notifications" />,
        },
        // {
        //   id: 'mailchimp',
        //   text: 'Mailchimp',
        //   path: '',
        //   permission: Permission.ListSearchMailchimp,
        //   icon: <img src={mailchimp} alt="Mailchimp" />,
        // },
        // {
        //   id: 'firebase',
        //   text: 'Firebase',
        //   path: '',
        //   permission: Permission.ListSearchFirebase,
        //   icon: <img src={firebase} alt="Firebase" />,
        // },
      ],
    },
    {
      id: 'users',
      text: 'Registered Users',
      path: '',
      permission: Permission.OUAdmin,
      icon: <img src={subscribers} alt="Registered Users" />,
    },

    {
      id: 'settings',
      text: 'Settings',
      path: '',
      icon: <img src={settings} alt="Settings" />,
      children: [
        {
          id: 'settings-admin',
          text: 'Admin (Legacy)',
          path: '/legacy-admin',
          permission: Permission.OUSuperAdmin,
          icon: <img src={adminLegacy} alt="Admin Legacy" />,
        },
        {
          id: 'admin',
          text: 'Admin',
          path: '/settings/admin',
          permission: Permission.OUAdmin,
          icon: <img src={admin} alt="Admin" />,
        },
        {
          id: 'advancedAdmin',
          text: 'Advanced Admin',
          path: '/settings/advanced-admin',
          permission: Permission.OUSuperAdmin,
          icon: <img src={advancedAdmin} alt="Advanced Admin" />,
        },
        {
          id: 'ouNavigation',
          text: 'Navigation',
          path: '/settings/ou-navigation',
          permission: Permission.OUSuperAdmin,
          icon: <img src={navigation} alt="Navigation" />,
        },
        {
          id: 'mrssFeedConfig',
          text: 'MRSS Feed Config',
          path: '/settings/mrss-feed-config',
          permission: Permission.OUSuperAdmin,
          icon: <img src={mrssFeed} alt="Mrss Feed" />,
        },
        {
          id: 'mrssMapper',
          text: 'MRSS Mapper',
          path: '/settings/mrss-mapper',
          permission: Permission.OUSuperAdmin,
          icon: <img src={mrssMapper} alt="mrssMapper" />,
          children: [],
        },
        {
          id: 'globalInterstitials',
          text: 'Global Interstitials',
          path: `/channelmanager/editor/${ouData?.ouId}`,
          icon: <img src={globalInterstitials} alt="globalInterstitials" />,
          children: [],
        },
        {
          id: 'transitionPackages',
          text: 'Automated Transitions',
          path: '/transitiontemplates/list',
          permission: Permission.ListSearchTransitionTemplates,
          icon: <img src={transitionPackages} alt="transitionPackages" />,
        },
        {
          id: 'eventWorkflows',
          text: 'Event Workflows',
          path: '/event-workflows',
          permission: Permission.EventWorkflow,
          icon: <img src={workflowConfig} alt="workflowConfig" />,
        },
      ],
    },
    {
      id: 'site',
      text: 'App Settings',
      path: '/site/general',
      permission: Permission.OUAdmin,
      icon: <img src={siteSettings} alt="App Settings" />,
      // featureFlagEnable: ouData.metaData.enableSite,
    },
    {
      id: 'workflowConfig',
      text: 'Workflow Config',
      path: '/workflow-config',
      icon: <img src={workflowConfig} alt="Work flow Config" />,
      permission: Permission.OUSuperAdmin,
    },
    {
      id: 'account-dropdown',
      text: 'Account',
      icon: <img src={account} alt="Account" />,
      path: '',
      permission: Permission.OUAdmin,
      children: [
        // {
        //   id: 'profile',
        //   text: 'Profile',
        //   path: '',
        //   icon: <span className="no-icon" />,
        // },
        {
          id: 'account',
          text: 'CMS Users',
          path: '/account/cms-user-management',
          icon: <img src={cmsUser} alt="CMS Users" />,
        },
        {
          id: 'appUsers',
          text: 'App Users',
          path: '/account/app-users',
          icon: <img src={account} alt="App Users" />,
        },
        {
          id: 'billing',
          text: 'Billing',
          path: '/account/billing',
          icon: <img src={billing} alt="Billing" />,
        },
        {
          id: 'accountActivity',
          text: 'Account Activity',
          path: '/account/audit-trail',
          icon: <img src={accountActivity} alt="Account Activity" />,
        },
      ],
    },
  ] as SlidebarItem[];
};

export default getSideBarList;
